import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, Lock, Person, School } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// School colors matching the homepage and dashboard
const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#e3f2fd',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#ffffff',
};

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: schoolColors.white,
  borderRadius: 24,
  boxShadow: `0 20px 40px ${schoolColors.primary}20`,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 8,
    background: `linear-gradient(90deg, ${schoolColors.primary}, ${schoolColors.accent}, ${schoolColors.gold})`,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  borderRadius: 8,
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    transform: 'translateY(-2px)',
    boxShadow: `0 10px 20px ${schoolColors.primary}40`,
  },
  '&:disabled': {
    background: `${schoolColors.primary}60`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.3s ease',
    '&:hover fieldset': {
      borderColor: schoolColors.accent,
    },
    '&.Mui-focused fieldset': {
      borderColor: schoolColors.primary,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: schoolColors.primary,
  },
}));

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

   const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', {
        username: formData.username.trim(),
        password: formData.password,
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('token_type', response.data.token_type);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (!response.data.user.is_admin) {
          navigate('/admin/marks-entry');
          return;
        }
      }

      navigate('/admin/dashboard');
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8, minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <StyledPaper elevation={3}>
        {/* Decorative header */}
        <Box sx={{ 
          bgcolor: schoolColors.primary, 
          py: 4, 
          textAlign: 'center',
          borderBottom: `3px solid ${schoolColors.gold}`,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <School sx={{ fontSize: 50, color: schoolColors.gold }} />
          </Box>
          <Typography variant="h5" sx={{ color: schoolColors.white, fontWeight: 600, mb: 1 }}>
            Kaozya High School
          </Typography>
          <Typography variant="body2" sx={{ color: schoolColors.gold, letterSpacing: 1 }}>
            "Quality education for better future"
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Lock sx={{ fontSize: 50, color: schoolColors.accent, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.primary }}>
              Admin Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Enter your credentials to access the administration portal
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: schoolColors.primary }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: schoolColors.primary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: schoolColors.primary }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <StyledButton
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 4, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: schoolColors.white }} /> : 'Login to Dashboard'}
            </StyledButton>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              This area is restricted to school administrators only.
            </Typography>
          </Box>
        </Box>

        {/* Decorative footer */}
        <Box sx={{ 
          bgcolor: `${schoolColors.light}`, 
          py: 2, 
          textAlign: 'center',
          borderTop: `1px solid ${schoolColors.gold}30`,
        }}>
          <Typography variant="caption" sx={{ color: schoolColors.primary }}>
            © 2026 Kaozya High School. All rights reserved.
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default AdminLogin;
